import React, { useState, useEffect } from "react";

interface ConfirmActionModalProps {
  isOpen: boolean;
  actionType: "terminate" | "delete" | null;
  onClose: () => void;
  onConfirm: (comment?: string) => Promise<void>;
}

const ConfirmActionModal: React.FC<ConfirmActionModalProps> = ({
  isOpen,
  actionType,
  onClose,
  onConfirm,
}) => {
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setComment("");
      setError("");
    }
  }, [isOpen, actionType]);

  if (!isOpen || !actionType) return null;

  const handleConfirmClick = async () => {
    if (actionType === "terminate" && !comment.trim()) {
      setError("Comment is required to terminate.");
      return;
    }
    await onConfirm(actionType === "terminate" ? comment.trim() : undefined);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          {actionType === "terminate" ? "Confirm Termination" : "Confirm Deletion"}
        </h3>

        {actionType === "terminate" ? (
          <>
            <label htmlFor="comment" className="block text-gray-700 mb-2">
            <span className="text-red-500">*</span>
             Please enter a comment to terminate:
            </label>
            <textarea
              id="comment"
              rows={4}
              className={`w-full border rounded p-2 focus:outline-none focus:ring-2 ${
                error ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"
              }`}
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
                if (error) setError("");
              }}
            />
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
          </>
        ) : (
          <p className="text-gray-700 mb-4">Are you sure you want to delete this record?</p>
        )}

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400 text-gray-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirmClick}
            className={`px-4 py-2 rounded text-white bg-red-600  ${
              actionType === "terminate" && !comment ? "" : "hover:bg-red-700"
            }`}
            disabled={actionType === "terminate" && !comment}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmActionModal;
